import { useEffect } from 'react'
import { usePromise } from 'shared/hooks/usePromise'
import { useDebounce } from 'shared/hooks/useDebounce'
import { DEBOUNCE_INPUT_TIMEOUT } from 'shared/constants'
import { processProviders } from './utils'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'
import { streamerApi } from '@/pages/Donation/api/streamer'
import { IFiatProvider } from '@/pages/Donation/api/streamer/types'

type Props = {
  fiatAmount: string | null
  baseCurrencyCode?: string
  setSelectedFiatProvider: (provider: IFiatProvider | null) => void
  country: string
  state?: string
}

export const useFiatProviders = ({ fiatAmount, baseCurrencyCode, setSelectedFiatProvider, country, state }: Props) => {
  const { username } = useStreamerConfigContext()

  const { send, data, isLoading, reset, error } = usePromise(streamerApi.calculateFiatAmount)
  const [debounceSend] = useDebounce(send, DEBOUNCE_INPUT_TIMEOUT)
  const { data: fiatProviders = [] } = data ?? {}
  const processedProviders = processProviders(fiatProviders)

  const sendRequest = (debounce: boolean) => {
    const isStateValid = country !== 'US' || !!state
    const shouldSend = isStateValid && country && username && fiatAmount && baseCurrencyCode
    const fetch = debounce ? debounceSend : send

    if (shouldSend) {
      fetch(username, {
        amount: fiatAmount.toString(),
        currency: baseCurrencyCode,
        country,
        state,
      })
    } else {
      reset()
      setSelectedFiatProvider(null)
    }
  }

  useEffect(() => {
    sendRequest(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseCurrencyCode, country, username, state])

  useEffect(() => {
    sendRequest(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiatAmount])

  return {
    fiatProviders: processedProviders,
    isProvidersLoading: isLoading,
    isFiatProvidersError: !!error,
  }
}
